import type { C1_Room } from "./C1_Room";
import type { C1_Wall } from "./C1_Wall";
import { transferRoomContents } from "./C1_roomContents";

type Point = { x: number; y: number };
type Boundary = { wall: C1_Wall; start: Point; end: Point };
const matches = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y) <= .001;

/** Extend an existing named room after placement and partner-wall rebuilding. */
export function mergeAdjacentRoomsWithSameName(added: C1_Room): C1_Room {
  let retained = added;
  const name = (added.owner?.name ?? "").trim();
  if (!name) return retained;
  while (true) {
    const shared = [...(retained.walls ?? [])].find(wall => {
      const rooms = wallMergeRooms(wall);
      return rooms?.some(room => room !== retained && (room.owner?.name ?? "").trim() === name);
    });
    if (!shared) return retained;
    // Keep the original room's identity, settings and TAG when adding its first extension.
    const neighbor = wallMergeRooms(shared)!.find(room => room !== retained)!;
    const target = retained === added ? neighbor : retained;
    mergeRoomsAtWall(shared, target);
    retained = target;
  }
}

/** Only an interior wall shared by two live rooms on the same floor can merge rooms. */
export function wallMergeRooms(wall: C1_Wall): [C1_Room, C1_Room] | undefined {
  const room = wall.parent?.c1 as C1_Room | undefined;
  const partner = wall.partnerWall;
  const other = partner?.parent?.c1 as C1_Room | undefined;
  if (wall.isDeleted || !partner || partner.isDeleted || !room?.owner || !other?.owner || room === other ||
      room.owner.isDeleted || other.owner.isDeleted || room.owner.parent !== other.owner.parent ||
      room.isExteriorSegment(wall) || other.isExteriorSegment(partner)) return;
  return [room, other];
}

/** Joins the remaining physical boundary, including split or concave imported polygons. */
export function mergedRoomBoundary(first: C1_Room, second: C1_Room): Boundary[] {
  const edges: Boundary[] = [];
  for (const [room, other] of [[first, second], [second, first]]) {
    for (const wall of room.walls ?? []) {
      if (wall.partnerWall?.parent === other.owner) continue;
      const points = room.wallPoints(wall);
      if (points) edges.push({ wall, ...points });
    }
  }
  const result: Boundary[] = [];
  if (edges.length) result.push(edges.shift()!);
  while (edges.length) {
    const end = result[result.length - 1].end;
    const index = edges.findIndex(edge => matches(edge.start, end) || matches(edge.end, end));
    if (index < 0) throw new Error("Die Räume haben keinen zusammenhängenden gemeinsamen Grundriss.");
    const edge = edges.splice(index, 1)[0];
    result.push(matches(edge.start, end) ? edge : { wall: edge.wall, start: edge.end, end: edge.start });
  }
  if (result.length < 3 || !matches(result[0].start, result[result.length - 1].end))
    throw new Error("Die Räume haben keinen geschlossenen gemeinsamen Grundriss.");
  return result;
}

/** Must run inside one undo transaction. Keeps the chosen room and persistent boundary/object IDs. */
export function mergeRoomsAtWall(wall: C1_Wall, retained: C1_Room): void {
  const rooms = wallMergeRooms(wall);
  if (!rooms || !rooms.includes(retained)) throw new Error("Diese Wand kann nicht gelöscht werden.");
  const removed = rooms.find(room => room !== retained)!;
  const boundary = mergedRoomBoundary(retained, removed);
  const survivingWalls = new Set(boundary.map(edge => edge.wall));
  const openings = rooms.flatMap(room => room.openings.map(opening => ({
    opening, wall: opening.wall, point: opening.absolutePosition,
  })));
  const furniture = rooms.flatMap(room => room.furnitures.map(item => ({
    item, point: item.equipmentPostitionGeometric(item.id), wall: item.dockWall,
  })));
  const devices = rooms.flatMap(room => room.interactiveEloItems.map(item => ({
    item, point: item.independentAbsolutePosition,
  })));
  for (const attached of openings) {
    if (!attached.wall || !survivingWalls.has(attached.wall)) attached.opening.owner?.delete();
  }
  // Native collection operations preserve ownership and ordering through undo/redo.
  transferRoomContents(removed, retained);
  retained.adoptWallBoundary(boundary);
  for (const room of rooms) {
    for (const segment of [...(room.walls ?? [])]) {
      if (!survivingWalls.has(segment)) segment.delete();
    }
  }
  for (const attached of openings) {
    if (!attached.wall || !survivingWalls.has(attached.wall) || !attached.point) continue;
    const points = retained.wallPoints(attached.wall)!;
    const dx = points.end.x - points.start.x, dy = points.end.y - points.start.y;
    const offset = ((attached.point.x - points.start.x) * dx + (attached.point.y - points.start.y) * dy) / (dx * dx + dy * dy);
    attached.opening.setWallPlacement(attached.wall, offset, attached.opening.reflect);
  }
  const rect = retained.innerRect();
  for (const { item, point, wall: dockWall } of furniture) {
    if (!point) continue;
    const docked = dockWall && survivingWalls.has(dockWall);
    item.setPosition({ x: (point.x - rect.x1) / (rect.x2 - rect.x1), y: (point.y - rect.y1) / (rect.y2 - rect.y1) },
      docked ? item.side : undefined, item.reflect, docked ? item.wallDocked : undefined, docked ? dockWall.id : undefined);
  }
  removed.owner!.delete();
  retained.recalculateFloorWallTypes();
  for (const { item, point } of devices) if (point) item.moveInteractiveTo(point);
}
